import React, { useEffect, useState } from 'react';
import {
  Button, Dialog, DialogTitle, DialogContent, DialogActions,
  RadioGroup, FormControlLabel, Radio, FormControl, FormLabel,
  Select, MenuItem, InputLabel, Alert, Box, Stack, Typography, CircularProgress,
  useMediaQuery, useTheme,
} from '@mui/material';
import { useListContext, useNotify, useRefresh, useUnselectAll, useTranslate } from 'react-admin';
import { httpClient } from '@/utils/httpClient';
import { useServerChoices } from '@/features/servers/useServerChoices';

type Slot = 'Primary' | 'Secondary';
type Scope = 'selected' | 'filtered';
type FilterValues = Record<string, unknown>;

interface BulkResult {
  matched: number;
  skippedSameServer: number;
  updated: number;
}

// Filter source names used by the account list (must match AccountList filters + backend binding).
const FILTER_KEYS = ['q', 'AccountStatus', 'LoggingStatus', 'LicenseId', 'PrimaryServerId', 'SecondaryServerId'];

const hasActiveFilter = (fv: FilterValues) =>
  FILTER_KEYS.some(k => fv?.[k] !== undefined && fv?.[k] !== null && fv?.[k] !== '');

const buildFilter = (fv: FilterValues) => ({
  search: fv?.q || undefined,
  accountStatus: fv?.AccountStatus || undefined,
  loggingStatus: fv?.LoggingStatus || undefined,
  licenseId: fv?.LicenseId || undefined,
  primaryServerId: fv?.PrimaryServerId || undefined,
  secondaryServerId: fv?.SecondaryServerId || undefined,
});

/**
 * Reassigns the primary or secondary server of many accounts at once: the selected rows, or every
 * account matching the list's filter. Reads selection and filter state from the list context, so
 * it must be rendered inside the account list.
 */
export const BulkServerReassignDialog: React.FC<{ open: boolean; onClose: () => void }> = ({ open, onClose }) => {
  const t = useTranslate();
  const theme = useTheme();
  const isPhone = useMediaQuery(theme.breakpoints.down('sm'));
  const { selectedIds = [], filterValues = {}, total = 0 } = useListContext();
  const notify = useNotify();
  const refresh = useRefresh();
  const unselectAll = useUnselectAll('accounts');
  const { choices: serverChoices } = useServerChoices();

  const [target, setTarget] = useState<Slot>('Secondary');
  const [scope, setScope] = useState<Scope>('filtered');
  const [newServerId, setNewServerId] = useState('');
  const [preview, setPreview] = useState<BulkResult | null>(null);
  const [previewing, setPreviewing] = useState(false);
  const [applying, setApplying] = useState(false);
  const [result, setResult] = useState<BulkResult | null>(null);

  const hasSelection = selectedIds.length > 0;
  const filterActive = hasActiveFilter(filterValues);
  const scopeReady = (scope === 'selected' && hasSelection) || (scope === 'filtered' && filterActive);

  const buildBody = (dryRun: boolean) => ({
    target,
    newServerId,
    dryRun,
    ...(scope === 'selected' ? { accountIds: selectedIds } : { filter: buildFilter(filterValues) }),
  });

  // Every opening starts over, scoped to the selection when there is one.
  useEffect(() => {
    if (!open) return;
    setScope(hasSelection ? 'selected' : 'filtered');
    setResult(null);
    setPreview(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleClose = () => {
    if (!applying) onClose();
  };

  // Dry-run preview whenever the parameters change.
  useEffect(() => {
    if (!open || result || !newServerId || !scopeReady) {
      setPreview(null);
      return;
    }
    let active = true;
    setPreviewing(true);
    httpClient
      .post<BulkResult>('/admin/account/BulkUpdateServers', buildBody(true))
      .then(res => { if (active) setPreview(res.data); })
      .catch(() => { if (active) setPreview(null); })
      .finally(() => { if (active) setPreviewing(false); });
    return () => { active = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, result, newServerId, target, scope, hasSelection, filterActive,
      selectedIds.length, JSON.stringify(filterValues)]);

  const handleApply = async () => {
    setApplying(true);
    try {
      const res = await httpClient.post<BulkResult>('/admin/account/BulkUpdateServers', buildBody(false));
      setResult(res.data);
      notify(t('accounts.bulk_server.success'), { type: 'success' });
      refresh();
      unselectAll();
    } catch (e) {
      notify(e instanceof Error && e.message ? e.message : t('ra.notification.http_error'), { type: 'error' });
    } finally {
      setApplying(false);
    }
  };

  const willChange = preview ? preview.matched - preview.skippedSameServer : 0;
  const canApply = !!newServerId && scopeReady && !!preview && preview.matched > 0 && !applying;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth fullScreen={isPhone}>
      <DialogTitle>{t('accounts.bulk_server.title')}</DialogTitle>
      <DialogContent dividers>
        {!result ? (
          <Stack spacing={2}>
            <FormControl>
              <FormLabel>{t('accounts.bulk_server.scope')}</FormLabel>
              <RadioGroup value={scope} onChange={(_, v) => setScope(v as Scope)}>
                <FormControlLabel
                  value="selected"
                  disabled={!hasSelection}
                  control={<Radio />}
                  label={t('accounts.bulk_server.scope_selected', { n: selectedIds.length })}
                />
                <FormControlLabel
                  value="filtered"
                  disabled={!filterActive}
                  control={<Radio />}
                  label={t('accounts.bulk_server.scope_filtered', { n: total })}
                />
              </RadioGroup>
              {!hasSelection && !filterActive && (
                <Alert severity="info" sx={{ mt: 1 }}>{t('accounts.bulk_server.need_scope')}</Alert>
              )}
            </FormControl>

            <FormControl>
              <FormLabel>{t('accounts.bulk_server.field')}</FormLabel>
              <RadioGroup row value={target} onChange={(_, v) => setTarget(v as Slot)}>
                <FormControlLabel value="Primary" control={<Radio />} label={t('resources.accounts.fields.primaryServer')} />
                <FormControlLabel value="Secondary" control={<Radio />} label={t('resources.accounts.fields.secondaryServer')} />
              </RadioGroup>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel id="bulk-server-new">{t('accounts.bulk_server.new_server')}</InputLabel>
              <Select
                labelId="bulk-server-new"
                value={newServerId}
                label={t('accounts.bulk_server.new_server')}
                onChange={e => setNewServerId(e.target.value)}
              >
                {serverChoices.map(s => <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>)}
              </Select>
            </FormControl>

            {newServerId && scopeReady && (previewing ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CircularProgress size={16} />
                <Typography variant="body2">{t('accounts.bulk_server.calculating')}</Typography>
              </Box>
            ) : preview && (
              <Alert severity={preview.matched === 0 ? 'warning' : 'info'}>
                {preview.matched === 0
                  ? t('accounts.bulk_server.preview_none')
                  : t('accounts.bulk_server.preview', { u: willChange, s: preview.skippedSameServer })}
              </Alert>
            ))}
          </Stack>
        ) : (
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>{t('accounts.bulk_server.result_title')}</Typography>
            <Typography variant="body2">{t('accounts.bulk_server.r_matched', { n: result.matched })}</Typography>
            <Typography variant="body2" color="success.dark">{t('accounts.bulk_server.r_updated', { n: result.updated })}</Typography>
            {result.skippedSameServer > 0 && (
              <Typography variant="body2" color="warning.dark">{t('accounts.bulk_server.r_skipped', { n: result.skippedSameServer })}</Typography>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" onClick={handleClose} disabled={applying}>
          {result ? t('ra.action.close') : t('ra.action.cancel')}
        </Button>
        {!result && (
          <Button variant="contained" onClick={handleApply} disabled={!canApply}>
            {applying ? <CircularProgress size={18} color="inherit" /> : t('accounts.bulk_server.apply')}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

/** The selection toolbar's entry to the bulk reassign: reassigns the selected rows. */
export const BulkServerReassignButton: React.FC = () => {
  const t = useTranslate();
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button variant="outlined" onClick={() => setOpen(true)}>
        {t('accounts.bulk_server.button')}
      </Button>
      <BulkServerReassignDialog open={open} onClose={() => setOpen(false)} />
    </>
  );
};
